import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, Award, GraduationCap } from 'lucide-react';
import Footer from '../components/Footer';
import { expertCounsellors } from '../data/mock';
import PageSeo from '../seo/PageSeo';

export default function CounsellorsPage() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50/90 via-white to-teal-50/40 flex flex-col">
      <PageSeo
        title="People behind your sessions | ChooseRightCareer"
        description="Every Career DNA report can lead to a real conversation. Here's who you may work with: what they care about, and how they think about guidance."
        path="/counsellors"
      />
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-blue-100 shadow-sm">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-blue-700 font-semibold hover:text-teal-700 transition-colors shrink-0"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to home
          </Link>
          <Link
            to="/"
            className="font-display text-xl font-bold bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent text-right"
          >
            ChooseRightCareer
          </Link>
        </div>
      </header>

      <main className="flex-1">
        <div className="container mx-auto px-6 py-12 md:py-16 max-w-5xl">
          <div className="text-center max-w-2xl mx-auto mb-14 md:mb-16">
            <div className="inline-flex items-center gap-2 bg-blue-100 px-4 py-2 rounded-full mb-6">
              <GraduationCap className="w-5 h-5 text-blue-700" />
              <span className="text-sm font-semibold text-blue-900">Expert counsellors</span>
            </div>
            <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
              People behind your sessions
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed">
              Every Career DNA report can lead to a real conversation. Here&apos;s who you may work
              with: what they care about, and how they think about guidance.
            </p>
          </div>

          <ul className="space-y-12 md:space-y-16 list-none p-0 m-0">
            {expertCounsellors.map((c) => (
              <li
                key={c.id}
                id={c.slug}
                className="scroll-mt-28 rounded-3xl border border-gray-200/80 bg-white/90 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
              >
                <div className="grid md:grid-cols-[min(280px,40%)_1fr] gap-0 md:gap-0">
                  <div className="relative aspect-[4/5] md:aspect-auto md:min-h-[280px] isolate overflow-hidden">
                    <img
                      src={c.image}
                      alt=""
                      width={400}
                      height={500}
                      loading="lazy"
                      decoding="async"
                      className="absolute inset-0 z-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 z-[1] bg-gradient-to-t from-black/50 via-transparent to-transparent md:bg-gradient-to-r md:from-transparent md:via-transparent md:to-black/10 pointer-events-none" />
                    <div className="absolute top-2 right-2 z-10 bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-full flex items-center gap-2 shadow-md ring-1 ring-white/70">
                      <Award className="w-4 h-4 text-blue-600 shrink-0" />
                      <span className="text-sm font-bold text-gray-900">{c.experience}</span>
                    </div>
                  </div>
                  <div className="p-8 md:p-10 flex flex-col justify-center">
                    <h2 className="font-display text-2xl md:text-3xl font-bold text-gray-900 mb-1">
                      {c.name}
                    </h2>
                    <p className="text-lg font-semibold text-blue-600 mb-4">{c.role}</p>
                    <p className="text-gray-700 leading-relaxed mb-4">{c.story}</p>
                    <p className="text-sm text-gray-500 leading-relaxed border-t border-gray-100 pt-4">
                      <span className="font-semibold text-gray-700">Focus: </span>
                      {c.expertise}
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ul>

          <div className="mt-14 text-center">
            <Link
              to="/#expert-counsellors"
              className="inline-flex items-center justify-center text-blue-700 font-semibold hover:text-teal-700 transition-colors"
            >
              See How sessions work ..click here
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
